"use strict";

const NewProjectInspectionController = function () {
  const htmlPath = "modals/newProjectInspection";
  const formKey = "project-inspection";

  let $modal;
  let tabs;
  let dataStore;

  var loadNewProjectInspection = async function (
    projectId,
    { readOnly = false, defaultData = {} } = {},
  ) {
    var newDataStore;

    try {
      newDataStore = await ResourceController.get("newConstructionProjectInspection").createNew(
        projectId,
      );
    } catch (e) {
      if (!(e instanceof DataStoreError)) {
        throw e;
      }

      return;
    }

    _setNewDefaultData(newDataStore, defaultData);
    _showProjectInspectionModal(newDataStore, readOnly);
  };

  var _setNewDefaultData = function (newDataStore, defaultData) {
    const existingData = newDataStore.getWithUpdates();
    const updateData = newDataStore.getForm();

    for (const key in defaultData) {
      updateData[key] = existingData[key] ?? defaultData[key];
    }

    newDataStore.setForm(updateData);
  };

  var loadExistingProjectInspection = async function (inspectionId, readOnly = false) {
    var newDataStore;

    try {
      newDataStore = await ResourceController.get("newConstructionProjectInspection").openExisting(
        inspectionId,
      );
    } catch (e) {
      if (!(e instanceof DataStoreError)) {
        throw e;
      }

      return;
    }
    _showProjectInspectionModal(newDataStore, readOnly);
  };

  var _showProjectInspectionModal = function (newDataStore, readOnly) {
    dataStore = newDataStore;
    tabs = [
      NewProjectInspectionGeneralInfo,
      NewProjectInspectionOpenFindings,
      NewProjectInspectionObservations,
      NewProjectInspectionSubmission,
    ];

    const renderData = mapTabProps(tabs);

    $modal = TabbedModal.showTabbedModal(formKey, renderData, tabs, htmlPath, { readOnly });
    TabbedModal.setHeaderText(getHeaderHtml(renderData));
    Form.initializeAndLoadListeners($modal, formKey, { dataStore });
    FormSettings.customizeForm($modal, formKey);
    loadDropzone();
    loadListeners();
    setAdditionalModalHandlers();
    onChangeInspectionType();
  };

  var loadListeners = function () {
    unloadListeners();
    FollowUpInformal.loadListeners($modal);
    $modal.on("2N:FormInput", ".compliant input, .condition select", rerenderSummary);
    $modal.on("2N:FormInput", "[name='inspection-type']", onChangeInspectionType);
  };

  var unloadListeners = function () {
    FollowUpInformal.unloadListeners($modal);
    $modal.off("2N:FormInput", ".compliant input, .condition select", rerenderSummary);
    $modal.off("2N:FormInput", "[name='inspection-type']", onChangeInspectionType);
  };

  var onChangeInspectionType = function () {
    NewProjectInspectionObservations.onChangeInspectionType($modal);
    toggleVisibleTabs();
    rerenderSummary();
  };

  var rerenderSummary = function () {
    NewProjectInspectionSubmission.renderSummary(getAllData());
  };

  var reloadListeners = function () {
    if (!$modal) {
      return;
    }

    Form.initializeDatePickers($modal, formKey);
  };

  var mapTabProps = function (tabs) {
    const data = dataStore.getWithUpdates();

    const tabProps = tabs.reduce((props, tab) => {
      return $.extend(true, props, tab.getProps(data));
    }, {});
    return {
      ...data,
      ...tabProps,
      base: Config.get().apiUrl,
    };
  };

  var getHeaderHtml = function (data) {
    return nunjucks.render("modals/newProjectInspection/projectInspectionHeader.njk", data);
  };

  var setAdditionalModalHandlers = function () {
    TabbedModal.setCancelHandler(() => {
      dataStore.discard();
      unloadListeners();
    });
    TabbedModal.setSaveHandler(saveButtonClick, false);
    TabbedModal.setFormHasUpdatesHandler(formHasUpdates);
  };

  var saveButtonClick = function () {
    return new Promise((resolve) => {
      const fields = NewProjectInspectionValidator.getMissingRequiredFields(getAllData());
      _saveOrWarnInvalid(fields);
      resolve(false);
    });
  };

  var _saveOrWarnInvalid = function (fields) {
    showSaveAndWarnModal(fields);
    highlightInvalidFields(fields);
  };

  var saveAndCloseModal = async function () {
    try {
      await Form.getReadyToSavePromise(formKey);
      if (dataStore.getForm().locked) {
        await dataStore.lock();
      } else {
        await dataStore.save();
      }

      TabbedModal.hide();
      unloadListeners();
      Tree.set("newConstructionProjectInspection", {});
    } catch (e) {
      return UploadValidator.reportError(e);
    }
  };

  var getDataToUpload = function () {
    return getUpdateData(dataStore.getInitial(), dataStore.getForm(), dataStore.isNew());
  };

  var getUpdateData = function (initialData, dataToUpload, isNew) {
    if (isNew) {
      dataToUpload = mergeDefaultsIntoUpdates(initialData, dataToUpload);
    }

    prepareObservations(dataToUpload.inspectionObservations, initialData);
    addInspectionScore(dataToUpload);

    if (isNew) {
      return {
        projectId: initialData.project?.id,
        updates: dataToUpload,
      };
    } else {
      return {
        id: initialData.id,
        updates: dataToUpload,
      };
    }
  };

  var mergeDefaultsIntoUpdates = function (initialData, dataToUpload) {
    const result = $.extend(true, {}, initialData, dataToUpload);
    return result;
  };

  var prepareObservations = function (inspectionObservationUpdates, initialData) {
    for (const constantId in inspectionObservationUpdates) {
      if (inspectionObservationUpdates[constantId].length === 0) {
        delete inspectionObservationUpdates[constantId];
        break;
      }

      const observationUpdates = inspectionObservationUpdates[constantId];
      for (let index in observationUpdates) {
        index = parseInt(index);
        const update = observationUpdates[index];

        const observation =
          initialData.openFindings?.[constantId]?.[index] ??
          initialData.inspectionObservations?.[constantId]?.find((o) => o.index === index) ??
          [];

        if (observation?.findingsCode) {
          update.findingsCode = observation.findingsCode;
        }
      }

      inspectionObservationUpdates[constantId] = filterDeletedAndUnsaved(
        inspectionObservationUpdates[constantId],
      );
    }
  };

  var addInspectionScore = function (dataToUpload) {
    const observationData = Tree.get("currentProjectInspectionObservationData");

    dataToUpload.score = observationData?.scoreNumber;
  };

  var filterDeletedAndUnsaved = function (array) {
    return array.filter(
      (item) => item && (item.deleted !== true || item.findingsCode !== undefined),
    );
  };

  var formHasUpdates = function () {
    return !Form.isFormEmpty(formKey);
  };

  var getAllData = function () {
    return dataStore.getWithUpdates("inspectionObservations");
  };

  var setFormData = function (path, data) {
    return Form.manuallySetFormDataField(formKey, path, data);
  };

  var getFormKey = function () {
    return formKey;
  };

  var showSaveAndWarnModal = function (fields) {
    const messages = fields.map((field) => field.message);
    const uniqueMessages = new Set(messages);

    const incompleteFields = {
      entityName: dataStore.getInitial().project.projectIdentifier,
      entityKind: "inspection",
      incompleteFieldNames: [...uniqueMessages],
    };
    const buttonCallbacks = {
      cancelCallback: null,
      saveCallback: saveAndCloseModal,
      submitCallback: function () {
        setFormData(["locked"], true);
        saveAndCloseModal();
      },
    };

    SaveIncompleteModal.showWarnSaveSubmitModal(incompleteFields, buttonCallbacks);
  };

  var highlightInvalidFields = function (fields) {
    const fieldNames = fields.map((field) => field.fieldName);
    CommonModalFunctions.highlightFormFields($modal, fieldNames);
  };

  var toggleVisibleTabs = function () {
    const inspectionType = getAllData()?.inspectionType ?? null;
    TabbedModal.toggleTab("observations", !!inspectionType);

    ["open-findings"].forEach(function (tabIdentifier) {
      const $availableDocuments = $modal.find(`#${tabIdentifier} .match`);
      TabbedModal.toggleTab(tabIdentifier, $availableDocuments.length > 0);
    });

    if (inspectionType?.includes("follow-up")) {
      ["general-info", "open-findings", "submission"].forEach((tabIdentifier) => {
        TabbedModal.toggleTab(tabIdentifier, true);
      });
      TabbedModal.toggleTab("observations", false);
    }
  };

  var loadDropzone = function () {
    const existingFiles = dataStore.getInitial().generalFiles ?? [];

    Form.initializeDropzone(formKey, $modal, {
      existingFiles: existingFiles,
      newFiles: dataStore.getForm().generalFiles,
      downloadCallback: ApiCalls.downloadConstructionProjectInspectionFile,
    });
  };

  var postUpdate = function () {
    ConstructionProjectGeoServerLayer.invalidateLayerData();
    ToDoListController.loadTodos();
  };

  return {
    getAllData,
    loadNewProjectInspection,
    loadExistingProjectInspection,
    saveAndCloseModal,
    setFormData,
    getFormKey,
    reloadListeners,
    getDataToUpload,
    _showProjectInspectionModal,
    getUpdateData,
    _saveOrWarnInvalid,
    postUpdate,
    _setNewDefaultData,
  };
};

module.exports = NewProjectInspectionController();

const NewProjectInspectionGeneralInfo = require("./newProjectInspectionGeneralInfo");
const NewProjectInspectionObservations = require("./newProjectInspectionObservations");
const NewProjectInspectionOpenFindings = require("./newProjectInspectionOpenFindings");
const NewProjectInspectionSubmission = require("./newProjectInspectionSubmission");

const ApiCalls = require("../../apiCalls");
const CommonModalFunctions = require("../../modals/commonModalFunctions");
const Config = require("../../config");
const Form = require("../../general/form");
const FormSettings = require("../../settings/formSettings");
const SaveIncompleteModal = require("../../modals/saveIncompleteModal");
const TabbedModal = require("../../general/tabbedModal");
const UploadValidator = require("../../general/uploadValidator");
const NewProjectInspectionValidator = require("./newProjectInspectionValidator");
const ResourceController = require("../../offline/resourceController");
const ToDoListController = require("../../mapFunctions/toDoListController");
const ConstructionProjectGeoServerLayer = require("../../constructionG2/constructionProjectGeoServerLayer");
const DataStoreError = require("../../errors/dataStoreError");
const Tree = require("../../tree");
const FollowUpInformal = require("../../inspection/followUpInformal");
